import { useState, useEffect, useMemo } from 'react';
import { ref, onValue, Unsubscribe } from 'firebase/database';
import { database } from '../firebase/firebase';
import { useAuth } from './useAuth';
import { getEventSelection } from '../utils/eventSelections';

interface CurrentUserInfo {
    /**
     * Who you are signed in as — never the participant you are standing in for.
     * `null` means no account, and the caller names that in the user's language.
     */
    name: string | null;
    userId: string | null;
    avatar: string | null,
    /**
     * The event participant currently being acted as, when that is someone other
     * than yourself. Shown as a separate line, never in place of `name`.
     */
    actingAsName: string | null;
    isAnonymous: boolean;
    isLoggedIn: boolean;
}

interface EventUserSnapshot {
    displayName?: string;
    avatar?: string | null;
}

export const useCurrentUserInfo = (eventId?: string): CurrentUserInfo => {
    const { user } = useAuth();

    // Names are edited from other screens (the profile page, the event owner's
    // participant editor) and by other people. Listening instead of reading once
    // is what keeps the header from needing a reload to catch up.
    const [accountName, setAccountName] = useState<string | null>(null);
    const [eventUser, setEventUser] = useState<EventUserSnapshot | null>(null);

    // Get current selected user ID to include in dependencies
    const selectedUserId = eventId ? getEventSelection(eventId) : null;

    // The participant whose row on the bill this person is filling in.
    const participantId = eventId ? (selectedUserId || user?.uid || null) : null;

    useEffect(() => {
        if (!user) {
            setAccountName(null);
            return;
        }

        return onValue(
            ref(database, `users/${user.uid}/displayName`),
            snapshot => setAccountName(snapshot.val() || null),
            error => {
                console.error('Error reading account display name:', error);
                setAccountName(null);
            }
        );
    }, [user]);

    useEffect(() => {
        if (!eventId || !participantId) {
            setEventUser(null);
            return;
        }

        const unsubscribe: Unsubscribe = onValue(
            ref(database, `eventUsers/${eventId}/${participantId}`),
            snapshot => setEventUser(snapshot.val() || null),
            error => {
                console.error('Error reading event user:', error);
                setEventUser(null);
            }
        );

        return unsubscribe;
    }, [eventId, participantId]);

    return useMemo(() => {
        // The header answers "who am I logged in as", and only that. It used to
        // answer "whose row am I filling in", which reads as the same question
        // and is not: picking someone else's row in an event would quietly
        // rename you, so the account you were actually signed in as disappeared
        // from the screen entirely.
        const participantName = eventUser?.displayName || null;

        if (user) {
            return {
                name: accountName || user.displayName || user.email || null,
                userId: user.uid,
                avatar: user.photoURL || null,
                // Only worth saying when the row is not your own.
                actingAsName: participantId && participantId !== user.uid ? participantName : null,
                isAnonymous: false,
                isLoggedIn: true
            };
        }

        // No account: the header says so plainly rather than borrowing the name
        // of whichever participant was picked.
        return {
            name: null,
            userId: participantId,
            avatar: null,
            actingAsName: participantName,
            isAnonymous: Boolean(participantId?.startsWith('~u_')),
            isLoggedIn: false
        };
    }, [user, participantId, accountName, eventUser]);
};
